import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, TableSortLabel,
} from '@mui/material';
import { PatientBoardConfig, PatientCardVM } from './types';
import PatientBoardRow from './PatientBoardRow';

export type SortDirection = 'asc' | 'desc';

interface Props {
  patients: PatientCardVM[];
  config: PatientBoardConfig;
  sortBy: string;
  sortDirection: SortDirection;
  onSort: (field: string) => void;
}

const PatientBoardTable: React.FC<Props> = ({ patients, config, sortBy, sortDirection, onSort }) => {
  if (patients.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No patients.</Typography>
      </Paper>
    );
  }

  const headers: { field: string; label: string; align?: 'center' }[] = [
    { field: 'name', label: 'Patient' },
    { field: 'disposition', label: config.scoreColumnLabel },
    { field: 'location', label: 'Location' },
    { field: 'physician', label: 'Physician' },
    { field: 'diagnosis', label: 'Primary Diagnosis' },
    { field: 'expectedDischarge', label: 'Expected Discharge' },
    { field: 'currentLOS', label: 'Current LOS' },
    { field: 'phase', label: 'Phase' },
    ...(config.showUrgency ? [{ field: 'urgency', label: 'Urgency' }] : []),
    { field: 'assigned', label: 'Assigned', align: 'center' as const },
  ];

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {headers.map(h => (
              <TableCell
                key={h.field}
                align={h.align}
                sortDirection={sortBy === h.field ? sortDirection : false}
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                <TableSortLabel
                  active={sortBy === h.field}
                  direction={sortBy === h.field ? sortDirection : 'asc'}
                  onClick={() => onSort(h.field)}
                >
                  {h.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.map(patient => (
            <PatientBoardRow key={patient.id} patient={patient} config={config} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PatientBoardTable;
